import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveCourse } from '../context/ActiveCourseContext';
import { syllabusAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import { BookOpen, AlertCircle, ChevronRight, Clock, Award, List, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const Syllabus = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { activeCourseId, activeCourseData } = useActiveCourse();
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    setDemoMode(!isAuthenticated);

    if (activeCourseId) {
      fetchSyllabus();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, activeCourseId]);

  const fetchSyllabus = async () => {
    try {
      const response = await syllabusAPI.getSyllabus(activeCourseId);
      if (response.data.success) {
        setSyllabus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching syllabus:', error);
      console.error('Failed to load syllabus');
    } finally {
      setLoading(false);
    }
  };

  const handleSubtopicClick = (subtopicId) => {
    navigate('/filtered', {
      state: {
        filterSubtopic: subtopicId,
        sourceType: 'syllabus'
      }
    });
  };

  if (!activeCourseId) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Course</h2>
            <p className="text-gray-600 max-w-md mx-auto">Please activate a course from dashboard.</p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                Syllabus Coverage
                {demoMode && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                    Demo Mode
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-2">
                Track your syllabus coverage by units and topics. Click on any subtopic to practice questions.
              </p>
            </div>

            {activeCourseData && (
              <div className="bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-400 flex items-center gap-3">
                <div>
                  <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Active Course</div>
                  <div className="font-bold text-gray-900">{activeCourseData.name}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {syllabus && (
          <div>
            {/* Course Info Card - Compact */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-400 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 text-blue-600 rounded-md">
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">{syllabus.subject}</h2>
                </div>

                <div className="flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md border border-gray-400">
                    <List className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-gray-600">Code: <span className="font-semibold text-gray-900">{syllabus.subjectCode}</span></span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md border border-gray-400">
                    <Award className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-gray-600">Full Marks: <span className="font-semibold text-gray-900">{syllabus.fullMarks}</span></span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md border border-gray-400">
                    <Clock className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-gray-600">Units: <span className="font-semibold text-gray-900">{syllabus.units?.length || 0}</span></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Units List - Compact */}
            <div className="space-y-3">
              {syllabus.units?.map((unit, unitIndex) => {
                const unitQuestionCount = unit.questionCount ||
                  (unit.topics?.reduce((sum, t) => sum + (t.questionCount || 0), 0) || 0);

                return (
                  <div key={unit.unitNumber} className="bg-white rounded-lg shadow-sm border border-gray-400 overflow-hidden hover:shadow-md hover:border-blue-300 transition-all duration-200">
                    {/* Unit Header - Clickable & Compact */}
                    <div
                      onClick={() => {
                        const chapterNum = unit.unitNumber.toString();
                        navigate('/filtered', {
                          state: {
                            filterSubtopic: `Chapter ${chapterNum}`,
                            sourceType: 'syllabus'
                          }
                        });
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 cursor-pointer hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group border-b border-gray-400"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-white border-2 border-indigo-400 rounded-md flex items-center justify-center text-indigo-600 font-bold text-sm shadow-sm group-hover:border-indigo-500 group-hover:bg-indigo-50 transition-all">
                            {unit.unitNumber}
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{unit.title}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">
                            {unitQuestionCount} {unitQuestionCount === 1 ? 'question' : 'Total Questions'}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                      </div>
                    </div>

                    {/* Topics List - Compact with Hover Lift & Indent */}
                    <div className="divide-y divide-gray-100">
                      {unit.topics?.map((topic, index) => (
                        <div
                          key={topic.subtopicId || index}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubtopicClick(topic.subtopicId);
                          }}
                          className="group flex items-center justify-between pl-16 pr-4 py-2.5 hover:bg-gradient-to-r hover:from-blue-50/70 hover:to-transparent hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 cursor-pointer"
                        >
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <span className="font-semibold text-indigo-500 text-xs min-w-[2.5rem] group-hover:text-indigo-600 transition-colors">{topic.subtopicId}</span>
                            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors truncate">
                              {topic.description}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            <span className="text-xs text-gray-500 font-medium min-w-[3rem] text-right group-hover:text-gray-700 transition-colors">
                              {topic.questionCount || 0} {topic.questionCount === 1 ? 'place spotted' : 'places spotted'}
                            </span>
                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!syllabus && !loading && (
          <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Syllabus Found</h3>
            <p className="text-gray-600 max-w-md mx-auto">No syllabus available for this course yet. Please check back later.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Syllabus;
